// ─────────────────────────────────────────────
//  timelineGeometry — where the CHART's track actually begins, and what that
//  means for the now-line and for a click on empty lane space.
//
//  PURE. No React, no DOM. Every function takes measured rects as arguments,
//  for the same reason `timelineDrag` does: jsdom has no layout, so anything
//  that measures for itself cannot be proved in a test.
//
//  ⚠️ THE TRACK DOES NOT BEGIN AT THE LABEL COLUMN. A chart row is
//      [ room label : leftW ][ row buttons : ~64px ][ track : flex 1 ]
//  and the row buttons only exist when the viewer may edit. The now-line was
//  drawn with `calc(leftW + (100% - leftW) * frac)` — a formula that knows about
//  the label column and nothing else — so on an editable board it landed ~64px
//  LEFT of the minute it claimed, and it also ignored the chart's own 12px
//  padding on both sides. On a wall board that line is how a room reads where it
//  stands in the day, so "roughly right" is not right.
//
//  The fix is to stop deriving the offset and MEASURE it, which is the same
//  measurement the drag already needs. One number, two consumers: the axis
//  label row and the now-line both sit over the track instead of near it.
// ─────────────────────────────────────────────

import { DEFAULT_CASE_MIN } from "./timeline";
import { DRAG_STEP_MIN, minuteAtX, snapMinutes } from "./timelineDrag";

const finite = (n) => typeof n === "number" && Number.isFinite(n);

// The latest hour a lane click may plant a case at.
//
// ⚠️ `minutesToHm` WRAPS AT MIDNIGHT (timeline.js:431 — `Math.floor(v/60) % 24`).
// A click at 23:30 plus a one-hour default would emit "00:30", an END BEFORE ITS
// START, which `validateCaseTimes` then refuses with a message about a time the
// operator never chose. Clamping into the range the assignment form can actually
// express (`END_TIME_OPTIONS` stops at 23:00) is the honest answer.
export const LANE_MAX_END_MIN = 23 * 60;

/**
 * How far the room track sits from the inside edge of the chart, in px.
 *
 * @param chartRect        the chart node's `getBoundingClientRect()`
 * @param chartBorderLeft  the chart's left border width — pass `node.clientLeft`.
 *                         `left` on an absolutely-positioned child is measured
 *                         from the containing block's PADDING box, and the
 *                         bounding rect includes the border, so the two differ
 *                         by exactly this.
 * @param trackRect        a room track's `getBoundingClientRect()`
 *
 * @returns px, or null when nothing has laid out yet (jsdom, a hidden chart, the
 *          first paint) — in which case the caller keeps its old fallback rather
 *          than drawing the line at zero.
 */
export function trackOffsetPx({ chartRect, chartBorderLeft = 0, trackRect } = {}) {
  if (!chartRect || !trackRect) return null;
  if (!finite(chartRect.left) || !finite(trackRect.left)) return null;
  if (!finite(trackRect.width) || !(trackRect.width > 0)) return null;
  const border = finite(chartBorderLeft) ? chartBorderLeft : 0;
  const offset = trackRect.left - (chartRect.left + border);
  // A negative offset means the two rects are not in the relationship this
  // assumes (a detached node, a re-parented chart). Fail closed.
  if (!finite(offset) || offset < 0) return null;
  return offset;
}

/**
 * The now-line's `left`, in px from the inside edge of the chart.
 *
 * Clamped to the track: a `nowMin` outside the drawn day parks the line at the
 * bound rather than escaping the chart. The caller still decides whether to
 * render it at all (today only, inside the axis window).
 */
export function nowLinePx({ offsetPx, trackWidth, nowMin, axisStart, span } = {}) {
  if (!finite(offsetPx) || !finite(trackWidth) || !(trackWidth > 0)) return null;
  if (!finite(nowMin) || !finite(axisStart) || !finite(span) || !(span > 0)) return null;
  const frac = Math.max(0, Math.min(1, (nowMin - axisStart) / span));
  return offsetPx + frac * trackWidth;
}

/**
 * A click on empty lane space → the times a new case should be proposed with.
 *
 * Start is the click x snapped to the drag step (the same 15 minutes, so the
 * value the modal is seeded with is one `TimeCombo` can display as selected);
 * end is start + `DEFAULT_CASE_MIN`, the constant the timeline model already
 * substitutes for a missing end.
 *
 * @returns { startMin, endMin, startTime, endTime } or null when the lane cannot
 *          be measured. Null means "do not open the dialog" — a create pages
 *          phones, so a gesture we cannot place must do nothing at all.
 */
export function laneCreateTimes({
  clientX,
  rect,
  axisStart,
  span,
  step = DRAG_STEP_MIN,
  durationMin = DEFAULT_CASE_MIN,
} = {}) {
  const raw = minuteAtX({ clientX, rect, axisStart, span });
  if (raw == null) return null;
  const snapped = snapMinutes(raw, step);
  if (snapped == null) return null;

  const dur = finite(durationMin) && durationMin > 0 ? durationMin : DEFAULT_CASE_MIN;
  const stepMin = finite(step) && step > 0 ? step : DRAG_STEP_MIN;

  const startMin = Math.max(0, Math.min(snapped, LANE_MAX_END_MIN - stepMin));
  const endMin = Math.max(startMin + stepMin, Math.min(startMin + dur, LANE_MAX_END_MIN));

  return {
    startMin,
    endMin,
    startTime: hm(startMin),
    endTime: hm(endMin),
  };
}

// Local, because `minutesToHm` wraps and this file's whole point is that the
// wrap is a bug at the edges. Values are already clamped to a real day above.
function hm(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
